import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { SearchOutlined } from "@ant-design/icons";
import { useVariableValue } from "@devcycle/react-client-sdk";
import styled from "@emotion/styled";
import { message, Skeleton } from "antd5";
import { Flex, Text } from "styles/utility-components";

import { withAppLayout } from "components/app_layout/AppLayout";
import { DetailsContent, DetailsHeader, DetailsPage } from "components/app_layout/DetailsLayout";
import SidebarButton from "components/my_feed/SidebarButton";
import {
  useMyFeedPageState,
  useSavedViews,
  useViewUrlCheck,
} from "components/my_feed/useMyFeedPageState";
import ViewsContainer from "components/my_feed/views_bar/ViewsContainer";
import { EmptyNotices } from "components/notices/EmptyNotices";
import { ExportNoticesButton } from "components/notices/ExportNoticesButton";
import { FilterForm, groupedKeys } from "components/notices/FilterForm";
import { DEFAULT_PAGINATION, NoticeTable } from "components/notices/NoticeTable";
import { QuickFilterBar } from "components/notices/QuickFilterBar";
import {
  ALL_NOTICE_COLUMNS,
  convertNoticeFiltersToApi,
  convertNoticeFiltersToSearchRequest,
  generateNoticeAnalysisUrl,
  getAppliedFilters,
  NoticeFilters,
  showSavedViewWarningBanner,
} from "components/notices/utils";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import { FeatureType } from "components/paywall/paywallUtils";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import Sidebar from "components/ui/sidebar/Sidebar";
import Badge from "lib/core_components/Badge";
import { ClearSelectedButton } from "lib/core_components/ClearSelectedButton";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { createUseDebounce } from "lib/debounce";
import { INLINE_FILTERS, NEW_NOTICES_SEARCH } from "lib/featureFlags";
import { ColumnSettingFieldEnum } from "lib/generated/app-api";
import { useNotices } from "lib/hooks/api/notices/useNotices";
import { useAllViews } from "lib/hooks/api/notices/views/useAllViews";
import { useRecordSearch } from "lib/hooks/api/useRecordSearch";
import { useSubscription } from "lib/hooks/subscription";
import { useUnsavedChangeWarning } from "lib/hooks/useUnsavedChangesWarning";
import { parseBooleanValue, useURLState } from "lib/hooks/useURLState";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { usePaywall } from "lib/providers/Paywall";
import { routes } from "lib/routes";
import { sysWarningDefault } from "lib/themes/colors";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import FilterDrawer from "../../components/filter_form/filter_drawer/FilterDrawer";
import { Input } from "../../components/form_components/Inputs";
import { NewNoticeTable } from "../../components/notices/NewNoticeTable";
import { assertCurrentUser } from "../../lib/currentUser";
import { useCreditsStatus } from "../../lib/hooks/api/credits/useCreditsStatus";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import { findKeybyValue } from "../../lib/utils";
import NoticeEmptyStateAction from "./components/NoticeEmptyStateAction";

import css from "./MyFeedPage.module.scss";

enum ViewName {
  Competitor = "Competitor activity",
  Partner = "Partner activity",
  Expiries = "Upcoming contract expiries",
  // Still in app, legacy view
  CompetitorPartner = "Competitor & partner activity",
}

function getAppliedFilterCount(filters: NoticeFilters) {
  return Object.entries(filters).reduce<number>((acc, [key, val]) => {
    if (Array.isArray(val)) {
      // procurementStageQualifications is a deprecated filter, so don't count it
      if (key === "procurementStageQualifications") {
        return acc;
      }

      return acc + (val.length > 0 ? 1 : 0);
    }
    if (typeof val === "object") {
      if (
        ("countries" in val && val.countries && val.countries.length > 0) ||
        ("regions" in val && val.regions && val.regions.length > 0)
      ) {
        return acc + 1;
      }

      const countriesIsEmpty = "countries" in val && val.countries && val.countries.length === 0;

      const regionsIsEmpty = "regions" in val && val.regions && val.regions.length === 0;

      const selectedIsEmpty = "selected" in val && val.selected && val.selected.length === 0;

      // if the object has countries, regions and selected, and they're all empty arrays, then don't count it
      if (countriesIsEmpty && regionsIsEmpty && selectedIsEmpty) {
        return acc;
      }

      // is it a date filter with a filter object
      if ("hideNulls" in val && "filter" in val) {
        // check if the filter object has any values or if hideNulls is true
        const shouldAdd =
          Object.values(val.filter || {}).filter((v) => v).length > 0 || val.hideNulls;
        return acc + (shouldAdd ? 1 : 0);
      }
      // is it a date filter without a filter object
      if ("hideNulls" in val || "to" in val || "from" in val) {
        const shouldAdd = Object.values(val).filter((v) => v).length > 0;
        return acc + (shouldAdd ? 1 : 0);
      }

      // is it a sort object so dont count it
      if ("order" in val && "field" in val) {
        return acc;
      }
      return acc + (Object.keys(val).length > 0 ? 1 : 0);
    }
    if (typeof val === "string") {
      return acc + (val.length > 0 ? 1 : 0);
    }
    if (typeof val === "boolean") {
      return acc + 1;
    }
    return acc + (val ? 1 : 0);
  }, 0);
}

const useDebounce = createUseDebounce(300);

export function MyFeedPage() {
  const isInlineFiltersEnabled = useVariableValue(INLINE_FILTERS, false);
  const {
    filters,
    setFilters,
    selectedView,
    setSelectedView,
    clearViewAndFilters,
    isLoadingViews,
    tableColumns,
    setTableColumns,
    hasUserModifiedView,
  } = useMyFeedPageState();
  const { data: allViews } = useAllViews();
  const { recentViews } = useSavedViews();

  useUnsavedChangeWarning(isInlineFiltersEnabled && hasUserModifiedView);

  const [messageApi, messageContext] = message.useMessage();
  useViewUrlCheck(messageApi);
  const { openPaywallModal } = usePaywall();

  const useOpenSearch = useVariableValue(NEW_NOTICES_SEARCH, false);
  const [activeGroups, setActiveGroups] = useState<string[]>([]);
  const [selectedNotices, setSelectedNotices] = useState<string[]>();
  const [openFilterDrawer] = useURLState<boolean | undefined>(
    "openFilterDrawer",
    false,
    parseBooleanValue,
  );
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(!!openFilterDrawer);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const hydratedRecentViews = recentViews.map((id: string) => allViews?.find((v) => v.id === id));

  const subscription = useSubscription();

  /**
   * We need to override applied legacy filters
   * Only set supplierMentionType if supplierGuids are set
   */
  const appliedFilters = useMemo((): NoticeFilters => {
    filters.supplierMentionType =
      filters.supplierGuids && filters.supplierGuids.length > 0
        ? filters.supplierMentionType
        : undefined;

    // clear signals filter as this is removed
    filters.signals = [];

    return filters;
  }, [filters]);

  const paywallFeature = useMemo((): FeatureType | undefined => {
    if (selectedView?.isStandardView) {
      switch (selectedView.name) {
        case ViewName.Competitor:
          return subscription.hasDataTypes("BUYERS|SUPPLIERS") ? undefined : "COMPETITORS";
        case ViewName.Partner:
          return subscription.hasDataTypes("BUYERS|SUPPLIERS") ? undefined : "PARTNERS";
        case ViewName.CompetitorPartner:
          return subscription.hasDataTypes("BUYERS|SUPPLIERS") ? undefined : "SUPPLIERS";

        default:
          return undefined;
      }
    }
    return undefined;
  }, [selectedView?.isStandardView, selectedView?.name, subscription]);

  const filterCount = getAppliedFilterCount(appliedFilters);

  const { logEvent } = useTracking();

  const debouncedSetFilters = useDebounce((filters: NoticeFilters) => {
    setFilters(filters);
  });

  const debounceTextSearch = useDebounce((text: string) => {
    logEvent(EventNames.freeSearch, {
      "Filter name": "Free search",
      "Action type": "Filter applied",
      "Data type": EventDataTypes.record,
    });
    setFilters({ ...appliedFilters, text });
  });

  const { control, getValues, trigger, watch } = useForm<{ text: string }>({
    defaultValues: {
      text: appliedFilters.text,
    },
    mode: "onChange",
    values: appliedFilters,
  });

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: ({ text }) => {
      debounceTextSearch(text);
    },
  });

  const user = assertCurrentUser();

  useCreditsStatus({
    teamId: user.team.id,
    onSuccess: (data) => {
      if (data.creditsStatus.creditLimitReached) {
        sessionStorage.setItem("awardCreditLimitReached", "true");
      }
    },
  });

  const showWarningBanner = showSavedViewWarningBanner(
    selectedView?.view.filterSettings?.procurementStageQualifications ?? [],
    selectedView?.view.filterSettings?.assignee ?? [],
  );

  const recordFilters = convertNoticeFiltersToApi(filters, DEFAULT_PAGINATION);
  const { isLoading: isLoadingRecords, data: recordData } = useRecordSearch(recordFilters, {
    enabled: !useOpenSearch,
  });

  const { isLoading: isLoadingNotices, data: noticeData } = useNotices(
    convertNoticeFiltersToSearchRequest(filters, DEFAULT_PAGINATION),
    false,
    {
      enabled: useOpenSearch,
    },
  );

  const isLoading = useOpenSearch ? isLoadingNotices : isLoadingRecords;
  const totalResults = useOpenSearch
    ? noticeData?.pagingInfo?.totalResults
    : recordData?.pagingInfo?.totalResults;

  const hasMarketAnalysisAccess = subscription.hasDataTypes("MARKET_ANALYSIS");
  const handleMarketAnalysisClick = (evt: React.MouseEvent, id: string | undefined) => {
    if (!hasMarketAnalysisAccess) {
      evt.preventDefault();
      openPaywallModal("STRATEGY", {
        requiredDataSubscription: "MARKET_ANALYSIS",
        strategyPaywall: true,
      });
    } else {
      logEvent(EventNames.marketAnalysisCreated, {
        viewId: id ?? "N/A",
      });
    }
  };

  const renderFilterBar = () => {
    return (
      <div className={css.filterBar}>
        <div className={css.filterItems}>
          {isInlineFiltersEnabled ? (
            <>
              <SidebarButton
                open={isSidebarOpen}
                onOpenChange={setIsSidebarOpen}
                appliedFiltersCount={getAppliedFilters(filters).length}
              />
              <Button
                as="a"
                href={generateNoticeAnalysisUrl(filters, selectedView?.id, hasUserModifiedView)}
                icon="barChart12"
                buttonType="secondary"
                onClick={(e) => handleMarketAnalysisClick(e, selectedView?.id)}
              >
                Analyse
                {!hasMarketAnalysisAccess && <UIcon icon={"lock01"} size={14} />}
              </Button>
            </>
          ) : (
            <>
              <form onSubmit={(e) => e.preventDefault()}>
                <Input
                  placeholder="Search notices"
                  name="text"
                  control={control}
                  prefix={<SearchOutlined />}
                  label=""
                  allowClear
                  className={css.searchInput}
                />
              </form>
              <Button
                buttonType="secondary"
                onClick={() => setIsFilterDrawerOpen(true)}
                icon="filterFunnel02"
              >
                Filters
              </Button>
            </>
          )}
        </div>
        {filterCount > 0 && (
          <div className={css.actionContainer}>
            <div className={css.clearSelected}>
              {selectedNotices && selectedNotices.length > 0 && (
                <>
                  <ClearSelectedButton
                    count={selectedNotices.length}
                    onClear={() => setSelectedNotices([])}
                  />
                  :
                </>
              )}
            </div>
            {selectedNotices && selectedNotices.length === 0 && (
              <EditColumnsButton
                selectedColumns={tableColumns}
                onNewColumnSettingChange={(columns) =>
                  setTableColumns(columns.map((c) => c.field) as ColumnSettingFieldEnum[])
                }
                allAvailableColumns={ALL_NOTICE_COLUMNS}
                dataType={EventDataTypes.record}
              />
            )}
            <ExportNoticesButton filters={filters} selectedNotices={selectedNotices} />
          </div>
        )}
      </div>
    );
  };

  const renderQuickFilterBar = () => {
    return (
      <div className={css.viewFilters}>
        <QuickFilterBar
          filters={filters}
          onClearFilter={(key) =>
            setFilters({
              ...filters,
              [key]: Array.isArray(filters[key]) ? [] : undefined,
            })
          }
          onFilterClick={(key) => {
            setIsFilterDrawerOpen(true);
            setActiveGroups([findKeybyValue(key, groupedKeys) || ""]);
          }}
          clearAllFilters={() => clearViewAndFilters()}
        />
      </div>
    );
  };

  const emptyStateMessage = {
    title: isInlineFiltersEnabled
      ? hydratedRecentViews?.length > 1
        ? "Recent searches"
        : "Apply filters to start your search"
      : "Select a view or some filters to get started",
    description: isInlineFiltersEnabled
      ? hydratedRecentViews?.length > 1
        ? "Jump back in..."
        : "Or try our templates to help you get started"
      : "Notices matching your search will appear here.",
  };

  return (
    <DetailsPage>
      <DetailsHeader borderBottom={!paywallFeature}>
        <ViewsContainer />
      </DetailsHeader>
      <>
        {paywallFeature ? (
          <PaywallBanner
            featureType={paywallFeature}
            className={paywallFeature === "AWARDS" ? undefined : css.fullHeightBanner}
          />
        ) : (
          <DetailsContent
            className={isInlineFiltersEnabled ? css.contentWithInlineFilters : css.content}
          >
            {isLoadingViews ? (
              <>
                <Skeleton.Input active />
                <Skeleton active />
              </>
            ) : (
              <>
                {showWarningBanner && <SavedViewWarningBanner />}
                {!isInlineFiltersEnabled && renderFilterBar()}
                {filterCount > 0 ? (
                  <>
                    {!isInlineFiltersEnabled && renderQuickFilterBar()}
                    {useOpenSearch ? (
                      <div className={css.tableContentArea}>
                        {isInlineFiltersEnabled && (
                          <Sidebar open={isSidebarOpen} offsetTop={73}>
                            <Flex column>
                              <FilterHeaderContainer>
                                <FilterHeader>Filters</FilterHeader>
                                <Badge size={20}>{getAppliedFilters(filters).length}</Badge>
                              </FilterHeaderContainer>
                              <FilterForm
                                resetKey={selectedView?.id}
                                onChange={debouncedSetFilters}
                                filters={filters}
                                activeGroups={activeGroups}
                                onActiveGroupsChange={setActiveGroups}
                                onClose={() => setIsFilterDrawerOpen(false)}
                                isSidebar
                                isLoadingResults={isLoading}
                                totalResults={totalResults}
                              />
                            </Flex>
                          </Sidebar>
                        )}
                        <div
                          className={
                            isInlineFiltersEnabled ? css.tableMainAreaNew : css.tableMainArea
                          }
                        >
                          {isInlineFiltersEnabled && (
                            <div className={css.filterBarContainer}>{renderFilterBar()} </div>
                          )}
                          <NewNoticeTable
                            filters={filters}
                            columnSettings={tableColumns.map((c) => c.field)}
                            onSortChange={(sort) => setFilters({ ...filters, sort })}
                            selectedRows={selectedNotices}
                            onSelectedRowsChange={(rows) => setSelectedNotices(rows)}
                            emptyText={
                              <EmptyNotices
                                setIsFilterDrawerOpen={setIsFilterDrawerOpen}
                                sidebar={{
                                  setIsSidebarOpen,
                                  isSidebarOpen,
                                }}
                              />
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <NoticeTable
                        filters={filters}
                        columnSettings={tableColumns.map((c) => c.field)}
                        onSortChange={(sort) => setFilters({ ...filters, sort })}
                        selectedRows={selectedNotices}
                        onSelectedRowsChange={(rows) => setSelectedNotices(rows)}
                        emptyText={
                          <EmptyNotices
                            setIsFilterDrawerOpen={setIsFilterDrawerOpen}
                            sidebar={{
                              setIsSidebarOpen,
                              isSidebarOpen,
                            }}
                          />
                        }
                      />
                    )}
                  </>
                ) : (
                  <div className={isInlineFiltersEnabled ? css.tableContentArea : undefined}>
                    {isInlineFiltersEnabled && (
                      <Sidebar open={isSidebarOpen} offsetTop={73}>
                        <Flex column>
                          <FilterHeaderContainer>
                            <FilterHeader>Filters</FilterHeader>
                            <Badge size={20}>{getAppliedFilters(filters).length}</Badge>
                          </FilterHeaderContainer>
                          <FilterForm
                            resetKey={selectedView?.id}
                            onChange={debouncedSetFilters}
                            filters={filters}
                            activeGroups={activeGroups}
                            onActiveGroupsChange={setActiveGroups}
                            onClose={() => setIsFilterDrawerOpen(false)}
                            isSidebar
                            isLoadingResults={isLoading}
                            totalResults={totalResults}
                          />
                        </Flex>
                      </Sidebar>
                    )}
                    <div className={isInlineFiltersEnabled ? css.tableMainAreaNew : css.emptyState}>
                      <EmptyState
                        type="startingState"
                        heading={emptyStateMessage.title}
                        description={emptyStateMessage.description}
                        size="large"
                        width="100%"
                        action={
                          isInlineFiltersEnabled ? (
                            <NoticeEmptyStateAction
                              hydratedRecentViews={hydratedRecentViews}
                              setSelectedView={setSelectedView}
                              setIsFilterDrawerOpen={setIsFilterDrawerOpen}
                            />
                          ) : (
                            <Flex gap={8}>
                              <Button
                                buttonType="secondary"
                                as="button"
                                icon="filterFunnel02"
                                onClick={
                                  isInlineFiltersEnabled
                                    ? () =>
                                        isSidebarOpen
                                          ? setIsSidebarOpen(false)
                                          : setIsSidebarOpen(true)
                                    : () => setIsFilterDrawerOpen(true)
                                }
                              >
                                Filter
                              </Button>
                              <Button
                                as="a"
                                href="https://help.stotles.com/filtering-and-saving-views-in-your-notice-feed"
                                target="_blank"
                                buttonType="secondary"
                              >
                                Learn more
                              </Button>
                            </Flex>
                          )
                        }
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </DetailsContent>
        )}
      </>
      {/* This is for the awards preview */}
      {paywallFeature === "AWARDS" && (
        <DetailsContent className={css.content}>
          <NoticeTable
            filters={filters}
            columnSettings={tableColumns.map((c) => c.field)}
            onSortChange={(sort) => setFilters({ ...filters, sort })}
            isPreview
          />
        </DetailsContent>
      )}

      <FilterDrawer open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)}>
        {isFilterDrawerOpen && (
          <FilterForm
            onClose={() => setIsFilterDrawerOpen(false)}
            onChange={debouncedSetFilters}
            filters={filters}
            activeGroups={activeGroups}
            onActiveGroupsChange={setActiveGroups}
          />
        )}
      </FilterDrawer>
      {messageContext}
    </DetailsPage>
  );
}

type AdminProps = {
  represented: {
    name: string;
    guid: string;
    owner_id: number | null;
    company_id: number;
  };
  company: {
    id: number;
    name: string;
  };
};

// TODO: remove as no longer needed
function SavedViewWarningBanner() {
  return (
    <Wrapper>
      <IconWrapper>
        <UIcon icon="alertCircle" color="#FFFFFF" fill={sysWarningDefault} />
      </IconWrapper>
      <Flex column gap={16}>
        <Flex column>
          <Text bold>This view will be deleted on 21.03.2025</Text>
          <Text>
            This view previously had the qualification or assignee filter applied which is no longer
            in use given our latest Opportunity feature release and will be deleted on 21st March
            2025. To see the notices you've qualified, go to the opportunities section to continue.
          </Text>
        </Flex>
        <Flex gap={10}>
          <Button as="a" href={routes.opportunities}>
            Go to opportunities
          </Button>
          <Button
            buttonType="secondary"
            as="a"
            href="https://help.stotles.com/migration-guide-qualified-notices-to-opportunities"
          >
            Go to help centre
          </Button>
        </Flex>
      </Flex>
    </Wrapper>
  );
}

const Wrapper = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.sysWarningSubtleAlt,
  borderRadius: 8,
  padding: 16,
  alignItems: "flex-start",
  gap: 10,
  alignSelf: "stretch",
}));

const IconWrapper = styled(Flex)({
  padding: "4px 0",
});

const FilterHeaderContainer = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  gap: 8,
  padding: "24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const FilterHeader = styled(Flex)(({ theme }) => ({
  color: theme.colors.sysTextDefault,
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "125%",
}));

function AdminBarComp(props: AdminProps) {
  return (
    <>
      <span>Viewing: Feed for list {props.represented.name} for</span>
      <a href={`/admin/companies/${props.represented.company_id}`} target="_blank" rel="noreferrer">
        {props.company.name}
      </a>
    </>
  );
}

export default withAppLayout(MyFeedPage, {
  pageName: "My Feed",
  AdminBarComp,
});
